import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';

import { getConnectedCloudFile } from '@/services/database/attachments';
import type { CloudAttachment, CloudStorageConnection } from '@/services/database/attachments';

import AttachmentDocumentDrawer from '../AttachmentDocumentDrawer';

interface ConnectedFileDocumentDrawerProps {
    connectionId: string;
    fileId: string;
    onClose: () => void;
}

const ConnectedFileDocumentDrawer = ({ connectionId, fileId, onClose }: ConnectedFileDocumentDrawerProps) => {
    const requestKey = JSON.stringify([connectionId, fileId]);
    const [state, setState] = useState<{
        attachment?: CloudAttachment;
        connection?: CloudStorageConnection;
        error?: string;
        loading: boolean;
        requestKey: string;
    }>({ loading: true, requestKey });

    useEffect(() => {
        const controller = new AbortController();
        void getConnectedCloudFile(connectionId, fileId, controller.signal)
            .then((file) => {
                if (!controller.signal.aborted) setState({ ...file, loading: false, requestKey });
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) return;
                captureException(error, { tags: { action: 'open-connected-file' } });
                setState({
                    error: 'This file could not be loaded from its connected account.',
                    loading: false,
                    requestKey,
                });
            });
        return () => controller.abort();
    }, [connectionId, fileId, requestKey]);

    return (
        <AttachmentDocumentDrawer
            {...(state.requestKey === requestKey ? state : { loading: true })}
            canComment={false}
            open
            resourceBreadcrumbLabel='Search'
            onAttachmentsBreadcrumbNavigate={onClose}
            onResourceBreadcrumbNavigate={onClose}
            onClose={onClose}
        />
    );
};

export default ConnectedFileDocumentDrawer;
